/**
 * useMFAEnrollmentCheck Hook
 *
 * Checks whether the currently authenticated user has enrolled
 * at least one second factor (SMS) via Firebase Auth.
 *
 * Source of truth: Firebase Auth's multiFactor(user).enrolledFactors
 * The Firestore `mfaEnrolled` flag is updated as a cache for quick UI checks
 * but Firebase Auth is always the canonical source.
 *
 * WORK-008: MFA enrollment enforcement is DELIBERATELY bypassed for the
 * beta (BUG-041/BUG-090 lineage). The bypass is controlled by the
 * VITE_MFA_ENROLLMENT_REQUIRED env flag — absent/off preserves the beta
 * bypass (isEnrolled: true, Firebase Auth never consulted); 'true' runs
 * the real enrollment check so MFAGuard in App.tsx enforces.
 *
 * GA LAUNCH BLOCKER — BUG-126: set VITE_MFA_ENROLLMENT_REQUIRED=true and
 * re-enable MFA enrollment enforcement before GA / any non-beta cohort.
 */

import { useState, useEffect } from 'react';
import { multiFactor } from 'firebase/auth';
import { doc, setDoc } from 'firebase/firestore';
import { useAuth } from '../contexts/AuthContext';
import { db } from '../lib/firebase';

interface MFAEnrollmentStatus {
  /** Whether the check has completed */
  loading: boolean;
  /** Whether the user has at least one enrolled MFA factor */
  isEnrolled: boolean;
  /** Number of enrolled factors */
  enrolledFactorCount: number;
}

export function useMFAEnrollmentCheck(): MFAEnrollmentStatus {
  // Read at render time (not module scope) so the flag is testable and
  // resolved per environment build.
  const enforcementRequired =
    import.meta.env.VITE_MFA_ENROLLMENT_REQUIRED === 'true';

  const { user } = useAuth();
  const [loading, setLoading] = useState(true);
  const [isEnrolled, setIsEnrolled] = useState(false);
  const [enrolledFactorCount, setEnrolledFactorCount] = useState(0);

  useEffect(() => {
    // Beta bypass: skip the check entirely — no Firestore sync, no
    // console errors on every page load (the original BUG-090 motivation).
    if (!enforcementRequired) {
      return;
    }

    if (!user) {
      setLoading(false);
      setIsEnrolled(false);
      setEnrolledFactorCount(0);
      return;
    }

    try {
      const enrolledFactors = multiFactor(user).enrolledFactors;
      const enrolled = enrolledFactors.length > 0;

      setIsEnrolled(enrolled);
      setEnrolledFactorCount(enrolledFactors.length);

      syncMfaFlag(user.uid, enrolled);
    } catch (err) {
      console.error('Error checking MFA enrollment:', err);
      setIsEnrolled(false);
      setEnrolledFactorCount(0);
    } finally {
      setLoading(false);
    }
  }, [user, enforcementRequired]);

  if (!enforcementRequired) {
    // Current beta behavior, now explicit config instead of hardcoded:
    // bypasses MFAGuard in App.tsx.
    return { loading: false, isEnrolled: true, enrolledFactorCount: 0 };
  }

  return { loading, isEnrolled, enrolledFactorCount };
}

/**
 * Sync the mfaEnrolled flag to the user's Firestore document.
 * This is a fire-and-forget operation — failures are logged but not blocking.
 */
async function syncMfaFlag(userId: string, enrolled: boolean): Promise<void> {
  try {
    const userRef = doc(db, 'users', userId);
    await setDoc(userRef, { mfaEnrolled: enrolled }, { merge: true });
  } catch (err) {
    // Non-critical — the Firestore flag is just a cache
    console.warn('Failed to sync mfaEnrolled flag to Firestore:', err);
  }
}
